import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import RebusCardSvg from "../components/games/RebusCardSvg";

function getApiBaseUrl() {
  const explicitApiUrl = process.env.REACT_APP_API_URL;
  if (explicitApiUrl) {
    return explicitApiUrl.replace(/\/$/, "");
  }

  const explicitSocketUrl = process.env.REACT_APP_SOCKET_URL;
  if (explicitSocketUrl && explicitSocketUrl !== "/") {
    return explicitSocketUrl.replace(/\/$/, "");
  }

  const { protocol, hostname, port, origin } = window.location;
  if ((hostname === "localhost" || hostname === "127.0.0.1") && (port === "3000" || port === "")) {
    return `${protocol}//${hostname}:3001`;
  }

  return origin;
}

function formatTimestamp(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString();
}

function PuzzlePreviewCard({ puzzle, theme }) {
  const frameClass = theme === "dark"
    ? "bg-slate-950 border-slate-700"
    : "bg-white border-slate-200";
  const labelClass = theme === "dark"
    ? "text-slate-200"
    : "text-slate-700";

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${frameClass}`}>
      <div className={`mb-3 text-xs font-semibold uppercase tracking-[0.2em] ${labelClass}`}>
        {theme} surface
      </div>
      <div className="rounded-2xl bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,rgba(148,163,184,0.10),transparent_60%)] p-4">
        {puzzle?.card ? (
          <RebusCardSvg
            card={{ ...puzzle.card, id: puzzle.id }}
            title={puzzle.answer || "Rebus preview"}
            className="mx-auto aspect-[4/3] w-full max-w-md"
            darkMode={theme === "dark"}
          />
        ) : puzzle?.imageUrl ? (
          <img
            src={puzzle.imageUrl}
            alt={puzzle.answer || "Rebus preview"}
            className="mx-auto max-h-[320px] w-full max-w-md rounded-xl object-contain"
          />
        ) : (
          <div className={`rounded-xl border border-dashed p-8 text-center text-sm ${labelClass}`}>
            No card or imageUrl defined for this puzzle.
          </div>
        )}
      </div>
    </div>
  );
}

export default function RebusPreview() {

  const [searchParams, setSearchParams] = useSearchParams();
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const selectedId = Number(searchParams.get("id")) || null;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  useEffect(() => {

    // Only allow in development
    if (process.env.NODE_ENV !== "development") {
        throw new Error("RebusPreview is only available in development mode");
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rebus/puzzles?ts=${Date.now()}`, {
          cache: "no-store",
        });
        const data = await response.json();

        if (cancelled) return;

        if (!response.ok || !data.ok) {
          throw new Error(data.error || "Failed to load Rebus puzzles");
        }

        setPuzzles(Array.isArray(data.puzzles) ? data.puzzles : []);
        setUpdatedAt(data.updatedAt || "");
        setError("");
      } catch (nextError) {
        if (!cancelled) {
          setError(String(nextError.message || nextError));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    const intervalId = window.setInterval(load, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [apiBaseUrl]);

  const selectedPuzzle = useMemo(() => {
    if (puzzles.length === 0) return null;
    return puzzles.find((puzzle) => puzzle.id === selectedId) || puzzles[0];
  }, [puzzles, selectedId]);

  useEffect(() => {
    if (!selectedPuzzle) return;
    if (selectedId === selectedPuzzle.id) return;
    setSearchParams({ id: String(selectedPuzzle.id) }, { replace: true });
  }, [selectedId, selectedPuzzle, setSearchParams]);

  return (

    
    <div className="mx-auto max-w-7xl p-4 text-slate-900 dark:text-slate-100">
      <div className="mb-6 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Rebus SVG Preview</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              This page polls the server copy of puzzles.json once per second. Save changes in the server file and the preview updates automatically without rebuilding the client.
            </p>
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Last file update: <span className="font-semibold text-slate-900 dark:text-slate-100">{formatTimestamp(updatedAt)}</span>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mb-6 rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
          <div className="font-semibold">Preview could not load the Rebus JSON.</div>
          <div className="mt-1 whitespace-pre-wrap">{error}</div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Puzzles</h2>
            <span className="text-xs text-slate-500 dark:text-slate-400">{puzzles.length}</span>
          </div>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                Loading puzzles...
              </div>
            ) : null}
            {!loading && puzzles.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                No puzzles found.
              </div>
            ) : null}
            {puzzles.map((puzzle) => {
              const active = puzzle.id === selectedPuzzle?.id;
              return (
                <button
                  key={puzzle.id}
                  type="button"
                  onClick={() => setSearchParams({ id: String(puzzle.id) })}
                  className={`w-full rounded-2xl border p-3 text-left transition ${active
                    ? "border-emerald-500 bg-emerald-50 shadow-sm dark:border-emerald-400 dark:bg-emerald-950/30"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/80 dark:hover:border-slate-700 dark:hover:bg-slate-900"
                  }`}
                >
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                    #{puzzle.id}
                  </div>
                  <div className="mt-1 font-semibold text-slate-900 dark:text-slate-100">{puzzle.answer || "Untitled puzzle"}</div>
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{puzzle.hint || "No hint"}</div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
            <div className="flex flex-col gap-2 border-b border-slate-200 pb-4 dark:border-slate-800 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                  Selected puzzle
                </div>
                <h2 className="mt-1 text-2xl font-black tracking-tight">{selectedPuzzle?.answer || "Choose a puzzle"}</h2>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                Hint: {selectedPuzzle?.hint || "None"}
              </div>
            </div>

            <div className="mt-5 grid gap-6 xl:grid-cols-2">
              <PuzzlePreviewCard puzzle={selectedPuzzle} theme="light" />
              <PuzzlePreviewCard puzzle={selectedPuzzle} theme="dark" />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Workflow</h3>
            <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
              <p>1. Keep this page open at /Tools/RebusPreview while your client and server dev processes are running.</p>
              <p>2. Edit server/gamecontent/rebus/puzzles.json and save.</p>
              <p>3. The page refetches the file every second and shows either the updated SVG or a JSON parse error.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}